import type {
  BusinessUnitPromptVersion,
  BusinessUnitPromptVersionsPage,
  CreateBusinessUnitPromptVersion,
} from 'services/types/businessUnitPromptVersion';

import baseApi from './baseApi';

const businessUnitPromptVersionsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getBusinessUnitPromptVersions: build.query<
      BusinessUnitPromptVersionsPage,
      { businessUnitId: number; limit?: number; offset?: number; search?: string }
    >({
      query: ({ businessUnitId, limit, offset, search }) => ({
        url: `/admin/businessUnits/${businessUnitId}/prompt-versions`,
        method: 'GET',
        params: { limit, offset, search: search || undefined },
      }),
      providesTags: (_result, _error, { businessUnitId }) => [
        { type: 'BusinessUnitPromptVersions', id: businessUnitId },
      ],
    }),
    getBusinessUnitPromptVersion: build.query<BusinessUnitPromptVersion, { businessUnitId: number; versionId: number }>(
      {
        query: ({ businessUnitId, versionId }) => ({
          url: `/admin/businessUnits/${businessUnitId}/prompt-versions/${versionId}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, { businessUnitId, versionId }) => [
          { type: 'BusinessUnitPromptVersions', id: businessUnitId },
          { type: 'BusinessUnitPromptVersions', id: `${businessUnitId}-${versionId}` },
        ],
      }
    ),
    createBusinessUnitPromptVersion: build.mutation<
      BusinessUnitPromptVersion,
      { businessUnitId: number; version: CreateBusinessUnitPromptVersion }
    >({
      query: ({ businessUnitId, version }) => ({
        url: `/admin/businessUnits/${businessUnitId}/prompt-versions`,
        method: 'POST',
        body: version,
      }),
      invalidatesTags: (_result, _error, { businessUnitId }) => [
        { type: 'BusinessUnitPromptVersions', id: businessUnitId },
      ],
    }),
    activateBusinessUnitPromptVersion: build.mutation<
      BusinessUnitPromptVersion,
      { businessUnitId: number; versionId: number; expectedActiveVersionId: number | null }
    >({
      query: ({ businessUnitId, versionId, expectedActiveVersionId }) => ({
        url: `/admin/businessUnits/${businessUnitId}/prompt-versions/${versionId}/activate`,
        method: 'PATCH',
        body: { expectedActiveVersionId },
      }),
      invalidatesTags: (_result, _error, { businessUnitId }) => [
        { type: 'BusinessUnitPromptVersions', id: businessUnitId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBusinessUnitPromptVersionsQuery,
  useGetBusinessUnitPromptVersionQuery,
  useLazyGetBusinessUnitPromptVersionQuery,
  useCreateBusinessUnitPromptVersionMutation,
  useActivateBusinessUnitPromptVersionMutation,
} = businessUnitPromptVersionsApi;
